import React, { createContext, useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAssessmentState from '../../lib/hooks/useAssessmentState';
import { postAssessmentStep } from '../api';
import { useLocation } from 'react-router-dom';
import { PartnerContext } from './PartnerContext';

export const AssessmentContext = createContext({});

export const AssessmentContextProvider = ({ children, ...props }) => {
  // Grab params and initialize navigation
  const { workflowId: routeWorkflowId, step: routeStep } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: assessmentState, isLoading, error } = useAssessmentState();
  const { state: partner } = useContext(PartnerContext);

  // Local shared state
  const [currentStepInfo, setCurrentStepInfo] = useState(null);
  const [currentStepChoices, setCurrentStepChoices] = useState(null);
  const [loadingNext, setLoadingNext] = useState(false);
  const [stepError, setStepError] = useState(null);
  const [responses, setResponses] = useState(null);
  const [progress, setProgress] = useState([]);
  const [workflowId, setWorkflowId] = useState(null);
  const [stepOverride, setStepOverride] = useState(null);
  const [userInfoComplete, setUserInfoComplete] = useState(false);
  const [photoSelectionComplete, setPhotoSelectionComplete] = useState(false);
  const [embeddedCodeInteracted, setEmbeddedCodeInteracted] = useState(false);

  // Add userInfoFieldIndex for UserInfo navigation
  const [userInfoFieldIndex, setUserInfoFieldIndex] = useState(0);
  // Add userInfoValues for UserInfo form state
  const [userInfoValues, setUserInfoValues] = useState({});

  // On first mount, initialize userInfoValues with backend/partner/responses if empty
  useEffect(() => {
    if (
      currentStepInfo?.type === 'user_info' &&
      (Object.keys(userInfoValues).length === 0 || !userInfoValues.user_info) &&
      currentStepInfo?.step &&
      currentStepInfo?.fields
    ) {
      // Build initial values like in UserInfo.js
      const backendValues = currentStepInfo.fields.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {});
      const partnerValues = partner || {};
      // Use backend keys as-is for form prefill
      const initialValues = { ...backendValues, ...partnerValues, ...responses };
      setUserInfoValues((prev) => {
        // Only set if prev is empty
        if (Object.keys(prev).length === 0 || !prev.user_info) {
          return initialValues;
        }
        return prev;
      });
    }
  }, [currentStepInfo, responses, partner]);
  // Derive effective step and workflow id based on override, URL, or assessment state
  const effectiveStep = stepOverride || routeStep || currentStepInfo?.step;
  const effectiveWorkflowId = routeWorkflowId || workflowId;

  const nextAllowed = useMemo(() => {
    if (typeof currentStepInfo?.type === 'string' && currentStepInfo.type.includes('result')) {
      return true;
    }
    if (currentStepInfo?.type === 'user_info') {
      return userInfoComplete;
    }
    if (currentStepInfo?.type === 'photo_selection') {
      return photoSelectionComplete;
    }
    if (currentStepInfo?.type === 'embedded_code') {
      return embeddedCodeInteracted;
    }
    return currentStepChoices ?? false;
  }, [currentStepChoices, currentStepInfo?.type, userInfoComplete, photoSelectionComplete, embeddedCodeInteracted]);

  const backAllowed = useMemo(() => {
    return true;
  }, [currentStepChoices, currentStepInfo?.type]);

  // Reset userInfoComplete when step changes
  useEffect(() => {
    if (currentStepInfo?.type !== 'user_info') {
      setUserInfoComplete(false);
    }
    if (currentStepInfo?.type !== 'photo_selection') {
      setPhotoSelectionComplete(false);
    }
    if (currentStepInfo?.type !== 'embedded_code') {
      setEmbeddedCodeInteracted(false);
    }
  }, [currentStepInfo?.type]);

  // Initialize state from assessmentState
  useEffect(() => {
    if (assessmentState) {
      setCurrentStepInfo(assessmentState.stepInfo || null);
      setProgress(assessmentState.progress || []);
      setResponses(assessmentState.responses || {});
      setWorkflowId(assessmentState.workflow_id);

      // Ensure selections is properly initialized as an array
      if (assessmentState.selections) {
        setCurrentStepChoices(Array.isArray(assessmentState.selections) ? assessmentState.selections : []);
      }
    }
  }, [assessmentState]);

  // Update state from either assessmentState or currentStepInfo
  useEffect(() => {
    // Handle initial data from API
    if (assessmentState?.stepInfo && !currentStepInfo) {
      setCurrentStepInfo(assessmentState.stepInfo);
      setProgress(assessmentState.progress || []);
      setResponses(assessmentState.responses || {});
      setWorkflowId(assessmentState.workflow_id);

      // Make sure selections is an array when coming from the API
      if (assessmentState.selections) {
        setCurrentStepChoices(Array.isArray(assessmentState.selections) ? assessmentState.selections : []);
      }
    }
    // Handle subsequent updates from step navigation
    else if (currentStepInfo) {
      setProgress(currentStepInfo.progress || []);
      setResponses(currentStepInfo.responses || {});
      setWorkflowId(currentStepInfo.workflow_id);
    }
  }, [assessmentState, currentStepInfo]);

  // Sync the URL with the effective workflow & step
  useEffect(() => {
    if (effectiveWorkflowId && effectiveStep) {
      const correctUrl = `/assessment/${effectiveStep}`;
      if (window.location.pathname !== correctUrl) navigate(correctUrl);
    }
  }, [effectiveWorkflowId, effectiveStep, navigate]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStepInfo]);

  // Action: post the next assessment step
  const handleNextStep = useCallback(
    async (userResponse) => {
      setLoadingNext(true);
      setStepError(null);
      try {
        const payload = {
          step: currentStepInfo?.step || null,
          response: currentStepChoices || { answer: userResponse },
          responses: responses || {},
          workflow_id: effectiveWorkflowId,
        };
        let mode;
        if (location.pathname.includes('/restart')) {
          mode = 'restart';
        }
        const data = await postAssessmentStep(payload, mode);

        if (data) {
          // ⛔ Stop further updates if redirect/kiosk logic applied
          const interrupted = handleCompletionRedirect(data);
          if (interrupted) return;

          // ✅ Otherwise continue updating context
          setCurrentStepInfo(data);
          setResponses(data.responses || {});
          setProgress(data.progress || []);
          setStepOverride(null);
        }
      } catch (err) {
        setStepError(err);
      } finally {
        setLoadingNext(false);
      }
    },
    [currentStepInfo, currentStepChoices, responses, effectiveWorkflowId],
  );

  // NEW: Auto-trigger the initial step if none is loaded.
  useEffect(() => {
    if (!currentStepInfo && !loadingNext && !stepError) {
      handleNextStep(null);
    }
  }, [currentStepInfo, loadingNext, stepError, handleNextStep, effectiveWorkflowId, responses]);

  const handleCompletionRedirect = (stepData) => {
    const isLastStep = stepData?.is_last_step;
    const isFinished = stepData?.finished;
    const signOutOnFinish = partner.sign_out_on_finish;
    const showCompletionScreen = partner.show_completion_screen;
    const isKioskMode = partner.is_kiosk;
    const redirectURI = localStorage.getItem('DIET_ID_REDIRECT');
    const nextUrl = redirectURI ?? partner.external_link_last_step_finish_button;
    const sharingToken = stepData?.sharingToken || assessmentState?.sharingToken;

    // Check kiosk state
    const urlParams = new URLSearchParams(window.location.search);
    const kioskCode = localStorage.getItem('kioskCode') || urlParams.get('access_code');
    const kioskSessionActive = isKioskMode && !!kioskCode;

    // Case 1: Handle immediate redirect case (no completion screen)
    if (isLastStep && !showCompletionScreen) {
      if (signOutOnFinish) {
        console.log('Logging out...');
        localStorage.clear(); // todo - use actual logout method
      }

      if (nextUrl) {
        console.log('Redirecting to:', nextUrl);
        window.location.href = nextUrl;
        return true;
      }
    }

    // Case 2: Finished with no explicit handling — go back to homepage
    if ((isLastStep || isFinished) && !showCompletionScreen && !kioskSessionActive && !nextUrl) {
      console.log('Assessment finished, no final step action configured. Returning to home.');
      window.location.href = '/';
      return true;
    }

    // Case 3: Handle Kiosk behavior
    if ((isLastStep || isFinished) && kioskSessionActive && sharingToken) {
      const { protocol, host } = window.location;
      const phiUrl = `${protocol}//${host}?detailed=1&phi=1#/s/${sharingToken}`;
      window.open(phiUrl, '_blank');
      window.location.href = `../../../kiosk?access_code=${kioskCode}`;
      return true;
    }

    if (!isLastStep && isFinished && showCompletionScreen) {
      console.log('Assessment finished, showing completion screen.');
      window.location.href = '/completion';
      return true;
    }

    return false;
  };

  const handlePreviousStep = useCallback(async () => {
    if (!currentStepInfo?.step) return;

    setLoadingNext(true);
    setStepError(null);

    try {
      const payload = {
        step: currentStepInfo.step,
        workflow_id: effectiveWorkflowId,
      };

      const data = await postAssessmentStep(payload, 'backwards');

      if (data) {
        const interrupted = handleCompletionRedirect(data);
        if (interrupted) return;

        setCurrentStepInfo(data);
        setResponses(data.responses || {});
        setProgress(data.progress || []);
        setStepOverride(null);
      }
    } catch (err) {
      setStepError(err);
    } finally {
      setLoadingNext(false);
    }
  }, [currentStepInfo, effectiveWorkflowId]);

  // Expose state and actions via context
  return (
    <AssessmentContext.Provider
      value={{
        state: {
          assessmentState,
          currentStepInfo,
          currentStepChoices,
          loadingNext,
          stepError,
          responses,
          progress,
          workflowId,
          stepOverride,
          effectiveStep,
          effectiveWorkflowId,
          nextAllowed,
          backAllowed,
          isLoading,
          error,
          userInfoComplete,
          photoSelectionComplete,
          userInfoFieldIndex,
          userInfoValues,
          embeddedCodeInteracted,
        },
        actions: {
          setCurrentStepInfo,
          setCurrentStepChoices,
          setLoadingNext,
          setStepError,
          setResponses,
          setProgress,
          setWorkflowId,
          setStepOverride,
          handlePreviousStep,
          handleNextStep,
          setUserInfoComplete,
          setPhotoSelectionComplete,
          setUserInfoFieldIndex,
          setUserInfoValues,
          setEmbeddedCodeInteracted,
        },
      }}
      {...props}
    >
      {children}
    </AssessmentContext.Provider>
  );
};
